import { useEffect, useState } from "react";
import axios from "axios";
import { X, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  en: {
    overline: "[ New project ]",
    heading: "Let's talk.",
    name: "Name *",
    email: "Email *",
    company: "Company / Agency",
    subject: "Subject",
    message: "Message *",
    submit: "Send Message",
    sending: "Sending…",
    success: "Thank you for reaching out. I'll get back to you soon.",
    close: "Close",
  },
  pt: {
    overline: "[ Novo projeto ]",
    heading: "Vamos conversar.",
    name: "Nome *",
    email: "Email *",
    company: "Empresa / Agência",
    subject: "Assunto",
    message: "Mensagem *",
    submit: "Enviar Mensagem",
    sending: "Enviando…",
    success: "Obrigado pelo contato. Retorno em breve.",
    close: "Fechar",
  },
};

const EMPTY = { name: "", email: "", company: "", subject: "", message: "", website: "" };

export default function ContactModal() {
  const { lang } = useLang();
  const L = LABELS[lang] || LABELS.en;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setDone(false);
      setError("");
    };
    window.addEventListener("tico:open-contact", handler);
    return () => window.removeEventListener("tico:open-contact", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const inputCls =
    "w-full bg-transparent border border-[#262626] px-4 py-3 text-sm text-white focus:border-[#ff4500] outline-none transition-colors placeholder:text-neutral-600";

  return (
    <div
      data-testid="contact-modal"
      className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative w-full max-w-lg border border-[#262626] bg-[#0a0a0a] p-8 md:p-10 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="contact-form-close"
          onClick={() => setOpen(false)}
          aria-label={L.close}
          className="absolute top-5 right-5 w-10 h-10 border border-[#262626] hover:border-[#ff4500] hover:bg-[#ff4500] flex items-center justify-center transition-all duration-300 text-white hover:text-black"
        >
          <X size={16} />
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff4500] mb-3">
          {L.overline}
        </div>
        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-white mb-8">
          {L.heading}
        </h2>

        {done ? (
          <div data-testid="contact-success" className="py-6">
            <span className="inline-flex w-14 h-14 bg-[#ff4500] items-center justify-center mb-6">
              <ArrowUpRight size={22} className="text-black" />
            </span>
            <p className="text-neutral-300 text-lg leading-relaxed">{L.success}</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
            {/* Honeypot — invisible to humans, bots fill it */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", opacity: 0 }}
            />
            <input
              data-testid="contact-name"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={L.name}
              className={inputCls}
            />
            <input
              data-testid="contact-email"
              required
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder={L.email}
              className={inputCls}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                data-testid="contact-company"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                placeholder={L.company}
                className={inputCls}
              />
              <input
                data-testid="contact-subject"
                value={form.subject}
                onChange={(e) => set("subject", e.target.value)}
                placeholder={L.subject}
                className={inputCls}
              />
            </div>
            <textarea
              data-testid="contact-message"
              required
              rows={5}
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder={L.message}
              className={`${inputCls} resize-y`}
            />
            {error && (
              <p data-testid="contact-error" className="text-red-400 text-sm font-mono">
                {error}
              </p>
            )}
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={busy}
              className="btn-orange w-full py-4 text-[12px] uppercase tracking-[0.25em] font-medium disabled:opacity-50"
            >
              <span>{busy ? L.sending : L.submit}</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
